<template>
  <teleport to="body">
    <transition name="show-dialog" @after-enter="focusDialog">
      <div
        v-if="dialogVisible"
        class="dialog-overlay"
        :class="{ 'is-dark': darkMode }"
        tabindex="0"
        ref="dialogElement"
        @keydown="handleKeydown"
      >
        <button @click="closeDialog" class="dialog-overlay__close-btn">
          <i class="ct-icon-close" />
        </button>
        <div class="dialog-body" :class="bodyClass">
          <template v-if="hasNavButtons">
            <button
              class="dialog-body__nav-btn is-left"
              :disabled="navLeftBtnDisabled"
              type="button"
              @click="$emit('left-click')"
            >
              <i class="ct-icon-arrow-left" />
            </button>
            <button
              class="dialog-body__nav-btn is-right"
              type="button"
              :disabled="navRightBtnDisabled"
              @click="$emit('right-click')"
            >
              <i class="ct-icon-arrow-right" />
            </button>
          </template>
          <slot />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs, watch } from 'vue'

export default defineComponent({
  name: 'AppDialog',
  props: {
    modelValue: null,
    darkMode: Boolean,
    hasNavButtons: Boolean,
    navLeftBtnDisabled: Boolean,
    navRightBtnDisabled: Boolean,
    bodyClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'left-click', 'right-click', 'close'],
  setup(props, { emit }) {
    const { hasNavButtons, navLeftBtnDisabled, navRightBtnDisabled } =
      toRefs(props)
    const dialogElement = ref()

    const dialogVisible = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const closeDialog = () => {
      dialogVisible.value = false
      emit('close')
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (!hasNavButtons.value) return

      if (event.code === 'ArrowLeft' && !navLeftBtnDisabled.value) {
        emit('left-click')
      }

      if (event.code === 'ArrowRight' && !navRightBtnDisabled.value) {
        emit('right-click')
      }
    }

    const focusDialog = () => dialogElement.value?.focus()

    watch(dialogVisible, (newVal) => {
      const method = newVal ? 'add' : 'remove'
      document.body.classList[method]('overflow-y-hidden')
    })

    return {
      dialogVisible,
      dialogElement,
      focusDialog,
      closeDialog,
      handleKeydown,
    }
  },
})
</script>

<style lang="scss" scoped src="./Index.scss" />
